import React, { ReactElement, useState, useCallback } from 'react'
import logo from '../../assets/images/logo.svg'
import { Formik, Form, Field, FieldArray, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, useStripe } from '@stripe/react-stripe-js'
import axios from 'axios'

// Assuming these imports are correct
import { Button, Input, Text } from '../atoms'
import { ModalStripe } from '../molecules'
import PerceptionBarModal from '../molecules/PerceptionBarModal/PerceptionBarModal'
import ContactLinks from '../molecules/ContactLinks/ContactLinks'
import { toast } from 'react-toastify'

import { logger } from '../../utils'
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51R3zfcIh7t8lXfzQF20z2i7qsHCSrpY2IOFHTbRu9Et39HBVwqg3lcFZxBhVUnZNWK24txreP62lXXbNgzlYSvGz00lnPVMxEl'
const PRODUCT_PRICE_ID = 'price_1RH9NKIh7t8lXfzQHNJoQaov'
const PRODUCT_NAME = 'DopaGE Prevention Portal - 1 year'
const PRODUCT_PRICE_CENTS = 2500
if (!STRIPE_PUBLISHABLE_KEY?.startsWith('pk_')) { console.warn('Stripe Publishable Key seems invalid or is not set.') }
if (!PRODUCT_PRICE_ID?.startsWith('price_')) { console.warn('Stripe Product Price ID seems invalid or is not set.') }
const formatCurrency = (cents: number): string => {
  const amount = typeof cents === 'number' ? cents / 100 : 0
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount) } catch (error) { console.error('Intl.NumberFormat failed:', error); return `$${amount.toFixed(2)}` }
}
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : Promise.resolve(null)
interface StudentEmailItem {
  email: string
  firstName: string
  lastName: string
}

interface DiscountCode {
  code: string
  discountPercentage: number
  expiryDate: string
}

// API validation function for promo codes
const validatePromoCode = async (promoCode: string) => {
  try {
    const response = await fetch('https://buxj4tmda4.execute-api.us-east-2.amazonaws.com/default/stripeWebHook', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'VALIDATE_CODE',
        code: promoCode
      })
    });

    return await response.json();
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
interface FormValues { quantity: number, studentEmails: StudentEmailItem[] }
const CheckoutSchema = Yup.object().shape({
  quantity: Yup.number().min(1, 'Must purchase at least 1 item').integer('Quantity must be a whole number').required('Quantity is required'),
  studentEmails: Yup.array().of(Yup.object().shape({
    email: Yup.string().email('Invalid email format').required('Student email is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required')
  }))
    .required('Must provide student emails')
    .test('emails-match-quantity', 'Number of emails must match quantity', function (value) {
      const { quantity } = this.parent as FormValues
      if (!value && (quantity === undefined || quantity === null || quantity < 1)) return true
      return Array.isArray(value) && value.length === quantity
    })
})
// --- End Setup ---

// --- Main Checkout Component ---
const CheckoutForm = (): ReactElement | null => {
  const stripe = useStripe()
  const [formError, setFormError] = useState<string | null>(null)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [discountCode, setDiscountCode] = useState<DiscountCode | null>(null)
  const [promoCodeInput, setPromoCodeInput] = useState('DGE-')
  const [isValidatingCode, setIsValidatingCode] = useState(false)
  const [codeValidationResult, setCodeValidationResult] = useState<any>(null)

  // Handle discount code validation
  const validateDiscountCode = async (code: string) => {
    setIsValidatingCode(true)
    setCodeValidationResult(null)
    
    try {
      const result = await validatePromoCode(code)
      setCodeValidationResult(result)
      
      if (result.success && result.valid) {
        setDiscountCode({
          code: result.code,
          discountPercentage: result.discountPercentage,
          expiryDate: result.expiryDate
        })
      }
    } catch (error) {
      setCodeValidationResult({ success: false, error: 'Failed to validate code' })
    } finally {
      setIsValidatingCode(false)
    }
  }

  // Normalize any user input (typing or paste) to a single DGE- prefix and 6-char code
  const normalizePromoCode = (raw: string): string => {
    if (typeof raw !== 'string') return 'DGE-'
    const upper = raw.toUpperCase()
    // Remove leading DGE (with/without hyphen), strip non-alphanumerics, cap at 6 chars
    const tail = upper.replace(/^DGE-?/, '').replace(/[^A-Z0-9]/g, '')
    return 'DGE-' + tail.slice(0, 6)
  }

  const handlePromoCodeChange = (value: string) => {
    const normalized = normalizePromoCode(value)
    setPromoCodeInput(normalized)
    // Auto-validate when we have a complete code (DGE- plus 6 characters)
    if (/^DGE-[A-Z0-9]{6}$/.test(normalized)) {
      validateDiscountCode(normalized)
    }
  }

  const handleDiscountModalClose = () => {
    setShowDiscountModal(false)
    setPromoCodeInput('DGE-')
    setCodeValidationResult(null)
  }

  const handleDiscountContinue = () => {
    setShowDiscountModal(false)
    setPromoCodeInput('DGE-')
    setCodeValidationResult(null)
  }

  // Handle quantity changes
  const handleQuantityChange = useCallback((
    _currentQuantity: number,
    newQuantityStr: string,
    values: FormValues,
    setFieldValue: FormikProps<FormValues>['setFieldValue']
  ) => {
    let newQuantity = parseInt(newQuantityStr, 10)
    if (isNaN(newQuantity)) {
      newQuantity = newQuantityStr === '' ? 0 : values.quantity
    }

    const finalQuantityForState = Math.max(1, newQuantity)
    const quantityForEmailSync = Math.max(0, newQuantity)
    const currentQuantityValue = values.quantity

    if (finalQuantityForState !== currentQuantityValue && (!isNaN(parseInt(newQuantityStr, 10)) || newQuantityStr === '')) {
      setFieldValue('quantity', finalQuantityForState)
    }

    const currentEmailLength = values.studentEmails?.length ?? 0
    if (quantityForEmailSync !== currentEmailLength) {
      const currentEmails = Array.isArray(values.studentEmails) ? values.studentEmails : []
      let nextStudentEmails: StudentEmailItem[]

      if (quantityForEmailSync > currentEmails.length) {
        nextStudentEmails = [
          ...currentEmails,
          ...Array.from({ length: quantityForEmailSync - currentEmails.length }, () => ({ email: '', firstName: '', lastName: '' }))
        ]
      } else {
        nextStudentEmails = currentEmails.slice(0, quantityForEmailSync)
      }
      setFieldValue('studentEmails', nextStudentEmails, true)
    }
  }, [])

  const handleSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
    setFormError(null)
    if (stripe == null) {
      logger.error(new Error('Stripe.js not loaded'))
      setFormError('Payment system not ready.')
      actions.setSubmitting(false)
      return
    }

    if (!Array.isArray(values.studentEmails) || values.studentEmails.length !== values.quantity) {
      logger.error(new Error('Email count mismatch'))
      setFormError('Email count does not match quantity.')
      actions.setSubmitting(false)
      return
    }

    actions.setSubmitting(true)
    try {
      const response = await axios.post('https://aote22c7zh.execute-api.us-east-2.amazonaws.com/default/stripeCheckout', {
        priceId: PRODUCT_PRICE_ID,
        quantity: values.quantity,
        studentEmails: values.studentEmails.map(item => item.email), // Array of email strings for backward compatibility
        studentData: values.studentEmails.map(item => ({ // Array of objects with full details
          email: item.email,
          firstName: item.firstName,
          lastName: item.lastName
        })),
        discountCode: discountCode?.code || null,
        discountPercentage: discountCode?.discountPercentage || 0,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/checkout`
      })
      const sessionId = response?.data?.sessionId

      if (!sessionId) { throw new Error('Failed to create checkout session.') }
      const result = await stripe.redirectToCheckout({ sessionId })
      if (result && result.error) { throw result.error }
    } catch (error: any) {
      logger.error(`Checkout error: ${JSON.stringify(error)}`)
      const message = error?.response?.data?.message || error?.message || 'An unexpected error occurred.'
      setFormError(message)
      toast.error(message, { position: toast.POSITION.TOP_CENTER })
    } finally {
      actions.setSubmitting(false)
    }
  }

  const initialValues: FormValues = { quantity: 1, studentEmails: [{ email: '', firstName: '', lastName: '' }] }
  if (stripe == null) { return <div className='text-center p-10'>Loading Payment Form...</div> }

  return (
    <Formik initialValues={initialValues} validationSchema={CheckoutSchema} onSubmit={handleSubmit} validateOnMount enableReinitialize={false}>
      {({ values, errors, touched, isSubmitting, setFieldValue, isValid }: FormikProps<FormValues>) => {
        const baseTotalCents = PRODUCT_PRICE_CENTS * (values.quantity || 0)
        const discountAmount = discountCode ? (baseTotalCents * discountCode.discountPercentage / 100) : 0
        const totalCents = baseTotalCents - discountAmount

        return (
        // Responsive container - mobile: full width with padding, tablet: 90%, desktop: 700px max
          <div
            className='pt-2 sm:pt-5 px-4 sm:px-6 md:px-8 lg:px-0 stripe-checkout-container'
            style={{
              maxWidth: '700px',
              width: '100%',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {/* Use expandable prop without modifying the Modal component itself */}
            <ModalStripe tall>
              <Form className='flex flex-col w-full'>
                {/* Header Section */}
                <div className='mb-2 sm:mb-4 md:mb-6'>
                  <Text className='font-bold text-center text-xl sm:text-2xl md:text-3xl text-dopage-darkGreen' bold size='LG' text='Checkout' type='paragraph' />
                </div>

                {/* Main Content Area */}
                <div className='w-full space-y-2 sm:space-y-4 md:space-y-5 mb-3 sm:mb-6'>
                  {/* Product Info */}
                  <div className='p-2 sm:p-3 border border-gray-600 rounded-md' style={{ backgroundColor: '#1E201F' }}>
                    <Text className='font-semibold text-base sm:text-lg text-white inline' text={PRODUCT_NAME} />
                    <Text className='text-dopage-lightGreen text-sm sm:text-base inline' text={` | ${formatCurrency(PRODUCT_PRICE_CENTS)} per student`} />
                  </div>

                  {/* Quantity Input */}
                  <div>
                    <label htmlFor='quantity' className='block text-xs sm:text-sm font-medium text-gray-300 mb-0.5 sm:mb-1'>Quantity</label>
                    <Field name='quantity'>
                        {({ field }: any) => (
                            <Input
                                  id='quantity'
                                  data-testid='input-quantity'
                                  className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base sm:text-lg leading-tight min-h-[44px]'
                                  type='number' min='1' placeholder='Quantity' {...field}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    field.onChange(e)
                                    handleQuantityChange(values.quantity, e.target.value, values, setFieldValue)
                                  }}
                                />
                          )}
                      </Field>
                    {errors.quantity && touched.quantity
                        ? <div className='text-[#ff674c] pt-1 text-sm min-h-[20px]' aria-live='polite'>{errors.quantity}</div>
                        : <div className='min-h-[20px]' aria-hidden='true' />}
                  </div>

                  {/* Recipient Info */}
                  <FieldArray name='studentEmails'>
                    {() => (
                        <div className='space-y-2 sm:space-y-3 md:space-y-4'>
                            <div>
                                  <Text className='font-bold text-dopage-lightGreen text-base sm:text-lg md:text-xl' bold size='LG' text='Recipient Information' />
                                </div>
                            <div>
                                  <Text className='text-dopage-primary text-xs sm:text-sm md:text-base leading-snug' size='SM' text='Enter the email address and name of each student who will receive access to the Portal. If purchasing for yourself, enter your own email and name. Students must be 16 years old or older to use the Portal.' />
                                </div>
                            {values.studentEmails.map((_item, index) => (
                                  <div key={index} className='space-y-0'>
                                    {/* Email Field */}
                                    <div>
                                        <label htmlFor={`studentEmails.${index}.email`} className='block text-xs sm:text-sm font-medium text-gray-300 mb-0.5 sm:mb-1'>
                                            Student #{index + 1}
                                          </label>
                                        <Field name={`studentEmails.${index}.email`}>
                                            {({ field }: any) => (
                                                <Input
                                                    id={`studentEmails.${index}.email`}
                                                    data-testid={`input-student-email-${index}`}
                                                    className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base sm:text-lg leading-tight min-h-[44px]'
                                                    type='email'
                                                    placeholder='Email Address'
                                                    autoComplete='off'
                                                    {...field}
                                                  />
                                              )}
                                          </Field>
                                        {errors.studentEmails &&
                                                             typeof errors.studentEmails !== 'string' &&
                                                             errors.studentEmails[index] &&
                                                             typeof errors.studentEmails[index] === 'object' &&
                                                             (errors.studentEmails[index] as StudentEmailItem).email &&
                                                             (touched.studentEmails != null) &&
                                                             touched.studentEmails[index]?.email
                                            ? <div className='text-[#ff674c] pt-1 text-sm min-h-3' aria-live='polite'>
                                                {(errors.studentEmails[index] as StudentEmailItem).email}
                                                </div>
                                            : <div className='min-h-3' aria-hidden='true' />}
                                      </div>

                                    {/* Name Fields Row (First and Last Name - stacked on mobile, side-by-side on tablet+) */}
                                    <div className='flex flex-col sm:flex-row sm:space-x-3 space-y-0'>
                                        {/* First Name Field (Full width on mobile, Half width on tablet+) */}
                                        <div className='w-full sm:w-1/2'>
                                            <Field name={`studentEmails.${index}.firstName`}>
                                                {({ field }: any) => (
                                                    <Input
                                                        id={`studentEmails.${index}.firstName`}
                                                        data-testid={`input-student-firstname-${index}`}
                                                        className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base sm:text-lg leading-tight min-h-[44px]'
                                                        type='text'
                                                        placeholder='First Name'
                                                        aria-label='First Name'
                                                        autoComplete='off'
                                                        {...field}
                                                      />
                                                  )}
                                              </Field>
                                            {errors.studentEmails &&
                                                                 typeof errors.studentEmails !== 'string' &&
                                                                 errors.studentEmails[index] &&
                                                                 typeof errors.studentEmails[index] === 'object' &&
                                                                 (errors.studentEmails[index] as StudentEmailItem).firstName &&
                                                                 (touched.studentEmails != null) &&
                                                                 touched.studentEmails[index]?.firstName
                                                ? <div className='text-[#ff674c] pt-1 text-sm min-h-[20px]' aria-live='polite'>
                                                    {(errors.studentEmails[index] as StudentEmailItem).firstName}
                                                    </div>
                                                : <div className='min-h-[20px]' aria-hidden='true' />}
                                          </div>

                                        {/* Last Name Field (Full width on mobile, Half width on tablet+) */}
                                        <div className='w-full sm:w-1/2'>
                                            <Field name={`studentEmails.${index}.lastName`}>
                                                {({ field }: any) => (
                                                    <Input
                                                        id={`studentEmails.${index}.lastName`}
                                                        data-testid={`input-student-lastname-${index}`}
                                                        className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base sm:text-lg leading-tight min-h-[44px]'
                                                        type='text'
                                                        placeholder='Last Name'
                                                        aria-label='Last Name'
                                                        autoComplete='off'
                                                        {...field}
                                                      />
                                                  )}
                                              </Field>
                                            {errors.studentEmails &&
                                                                 typeof errors.studentEmails !== 'string' &&
                                                                 errors.studentEmails[index] &&
                                                                 typeof errors.studentEmails[index] === 'object' &&
                                                                 (errors.studentEmails[index] as StudentEmailItem).lastName &&
                                                                 (touched.studentEmails != null) &&
                                                                 touched.studentEmails[index]?.lastName
                                                ? <div className='text-[#ff674c] pt-1 text-sm min-h-[20px]' aria-live='polite'>
                                                    {(errors.studentEmails[index] as StudentEmailItem).lastName}
                                                    </div>
                                                : <div className='min-h-[20px]' aria-hidden='true' />}
                                          </div>
                                      </div>
                                  </div>
                                ))}
                          </div>
                      )}
                  </FieldArray>

                  {/* General Form Errors */}
                  {formError &&
                    <div className='text-[#ff674c] mt-4 p-3 bg-red-100 border border-red-400 rounded text-center text-sm' aria-live='polite'>
                        {formError}
                      </div>}
                  {typeof errors.studentEmails === 'string' && (touched.quantity || touched.studentEmails?.some(e => e?.email))
                    ? <div className='text-[#ff674c] mt-4 p-3 bg-red-100 border border-red-400 rounded text-center text-sm' aria-live='polite'>
                        {errors.studentEmails}
                        </div>
                    : null}
                </div>

                {/* Footer Section */}
                <div className='mt-auto pt-2 sm:pt-3 md:pt-4 border-t border-gray-700'>
                  {/* Discount Code Link */}
                  <div className='mb-1.5 sm:mb-2 text-center'>
                    <button
                      type='button'
                      onClick={() => setShowDiscountModal(true)}
                      className='text-dopage-lightGreen hover:text-white transition-colors text-xs sm:text-sm underline'
                    >
                      I have a discount code
                    </button>
                  </div>
                  
                  {/* Applied Discount Display */}
                  {discountCode && (
                    <div className='mb-1.5 sm:mb-2 text-center'>
                      <div className='text-dopage-lightGreen text-xs sm:text-sm'>
                        Applied: {discountCode.code} (-{discountCode.discountPercentage}%)
                      </div>
                      <div className='text-xs sm:text-sm text-gray-400'>
                        Discount: -{formatCurrency(discountAmount)}
                      </div>
                    </div>
                  )}
                  
                  <div className='mb-2 sm:mb-3 md:mb-4 text-center'>
                    <Text className='text-lg sm:text-xl font-semibold text-white' text={`Total: ${formatCurrency(totalCents)}`} />
                  </div>
                  <Button
                    id='button-proceed-to-payment'
                    data-testid='button-proceed-to-payment'
                    className='diamond-btn min-h-[48px] sm:h-[52px] w-full font-bold flex justify-center items-center text-black disabled:opacity-50 disabled:cursor-not-allowed'
                    type='submit'
                    loading={isSubmitting}
                    disabled={isSubmitting || !stripe || !isValid}
                  >
                    <Text className='text-black text-base sm:text-lg md:text-xl' size='BASE' text='Proceed to Payment' type='span' />
                  </Button>
                  <ContactLinks id='link-contact-checkout' className='mt-2 sm:mt-3 md:mt-4 text-center text-bottom' />
                </div>
                
                {/* Discount Code Modal */}
                <PerceptionBarModal
                  isOpen={showDiscountModal}
                  onClose={handleDiscountModalClose}
                  className='p-4 sm:p-6 bg-[#1E201F] rounded-lg max-w-[calc(100%-2rem)] sm:max-w-md w-full mx-4 relative z-50'
                  overlayClassName='fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-40'
                >
                  <div>
                      <h2 className='text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 text-center'>Enter Discount Code</h2>
                      
                      <div className='mb-3 sm:mb-4'>
                        <Input
                          type='text'
                          value={promoCodeInput}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePromoCodeChange(e.target.value)}
                          onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                            const pasted = e.clipboardData?.getData('text') ?? ''
                            const normalized = normalizePromoCode(pasted)
                            e.preventDefault()
                            setPromoCodeInput(normalized)
                            if (/^DGE-[A-Z0-9]{6}$/.test(normalized)) {
                              validateDiscountCode(normalized)
                            }
                          }}
                          placeholder='DGE-1F7698'
                          className='w-full py-3 px-4 text-base sm:text-lg leading-tight min-h-[44px]'
                          maxLength={10}
                        />
                      </div>
                      
                      {isValidatingCode && (
                        <div className='text-center mb-3 sm:mb-4'>
                          <Text className='text-dopage-lightGreen text-xs sm:text-sm' text='Validating code...' />
                        </div>
                      )}
                      
                      {codeValidationResult && (
                        <div className='mb-3 sm:mb-4'>
                          {codeValidationResult.success && codeValidationResult.valid ? (
                            <div className='text-center'>
                              <div className='text-dopage-lightGreen text-xs sm:text-sm mb-2'>
                                ✓ Valid code! {codeValidationResult.discountPercentage}% discount
                              </div>
                              <div className='text-gray-300 text-xs mb-3 sm:mb-4'>
                                Expires: {new Date(codeValidationResult.expiryDate).toLocaleDateString()}
                              </div>
                              <div className='text-center'>
                                <div className='text-xs sm:text-sm text-gray-400 mb-2'>
                                  Discount: -{formatCurrency((baseTotalCents * codeValidationResult.discountPercentage / 100))}
                                </div>
                              </div>
                              <Button
                                onClick={handleDiscountContinue}
                                className='diamond-btn min-h-[44px] sm:h-[40px] w-full font-bold flex justify-center items-center text-black'
                              >
                                <Text className='text-black text-base sm:text-lg' text='Continue' />
                              </Button>
                            </div>
                          ) : (
                            <div className='text-center'>
                              <div className='text-red-400 text-xs sm:text-sm mb-3 sm:mb-4'>
                                {codeValidationResult.message || 'Invalid discount code'}
                              </div>
                              <Button
                                onClick={handleDiscountModalClose}
                                className='diamond-btn min-h-[44px] sm:h-[40px] w-full font-bold flex justify-center items-center text-black'
                              >
                                <Text className='text-black text-base sm:text-lg' text='Cancel' />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {!codeValidationResult && !isValidatingCode && (
                        <div className='text-center'>
                          <Button
                            onClick={handleDiscountModalClose}
                            className='diamond-btn min-h-[44px] sm:h-[40px] w-full font-bold flex justify-center items-center text-black'
                          >
                            <Text className='text-black text-base sm:text-lg' text='Cancel' />
                          </Button>
                        </div>
                      )}
                  </div>
                </PerceptionBarModal>
              </Form>
            </ModalStripe>
          </div>
        )
      }}
    </Formik>
  )
}

// --- Main Page Component (wrapping with Elements provider) ---
function StripeCheckoutPage (): ReactElement {
  // Add custom styles to override Modal backgrounds and ensure mobile responsiveness
  React.useEffect(() => {
    // Create a style element
    const styleEl = document.createElement('style')
    // Define CSS to override Modal backgrounds and fix mobile constraints
    styleEl.innerHTML = `
            .stripe-checkout-container > div > div > div.diamond-corner > div.inner.bg-black {
                background-color: #1E201F !important;
            }
            .stripe-checkout-container > div {
                background-color: #1E201F !important;
            }
            
            /* Mobile responsive overrides */
            @media (max-width: 640px) {
                .stripe-checkout-container,
                .stripe-checkout-container > div,
                .stripe-checkout-container > div > div,
                .stripe-checkout-container > div > div > div {
                    max-width: 100% !important;
                    width: 100% !important;
                    min-width: 0 !important;
                }
            }
        `
    // Add the style element to the document head
    document.head.appendChild(styleEl)

    // Cleanup function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(styleEl)
    }
  }, [])
  // Inject global styles to force scrolling
  React.useEffect(() => {
    document.documentElement.style.overflow = 'auto'
    document.body.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.body.style.minHeight = '100vh'
    document.body.style.position = 'relative'

    // Cleanup
    return () => {
      document.documentElement.style.removeProperty('overflow')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('height')
      document.body.style.removeProperty('min-height')
      document.body.style.removeProperty('position')
    }
  }, [])

  if (!STRIPE_PUBLISHABLE_KEY || !stripePromise) {
    return (
      <div className='py-10 px-4 text-center text-red-500'>
        <h2 className='text-xl font-bold mb-2'>Configuration Error</h2>
        <p>Payment processing is currently unavailable. Please contact support.</p>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      overflow: 'auto',
      position: 'relative',
      backgroundColor: '#101111'
    }}
    >
      {/* Logo - centered on mobile, positioned top-left on desktop */}
      <header className='flex justify-center pt-4 pb-1 sm:absolute sm:left-6 sm:top-6 sm:pt-0 sm:pb-0 md:left-[40px] md:top-[40px]' aria-hidden='true'>
        <img src={logo} className='w-[80px] sm:w-[120px] md:w-[138px]' alt='logo' />
      </header>
      <Elements stripe={stripePromise}>
        <CheckoutForm />
      </Elements>
    </div>
  )
}

export default StripeCheckoutPage
